import {
  type GradeStudentWorkbookTarget,
  type GradeWorkbookProgressTarget,
  type GradeWorkbookScoringTarget,
  type GroupStudentWorkbook,
  type GroupStudentWorkbookListParams,
  type GroupWorkbookPageParams,
  type GroupWorkbookPageProgress,
  type LessonClassStudentWorkbookTarget,
  type LessonClassWorkbookProgressTarget,
  type LessonClassWorkbookScoringTarget,
  studentWorkbookApi,
  type StudentWorkbookAssignBody,
  type StudentWorkbookListItemDetail,
  type StudentWorkbookListParams,
  type StudentWorkbookPageProgress,
  type StudentWorkbookProgressListTarget,
  type StudentWorkbookProgressStatusBody,
  type StudentWorkbookRevision,
  type StudentWorkbookScoredProblemsTarget,
  type StudentWorkbookScoringPayload,
} from '@mathflat/domain/StudentWorkbook'

import { mpEventInfo } from '@/constant'
import {
  GroupWorkbook,
  GroupWorkbookPage,
  GroupWorkbookPageDetailColl,
  type Student,
  StudentWorkbook,
  StudentWorkbookPage,
  StudentWorkbookPageDetailColl,
} from '@/dto'
import ExtendedMap from '@/models/ExtendedMap'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

// 교재 목록은 거르는 조건과 페이지 조건을 따로 받아 한 쿼리로 합친다
type StudentWorkbookFilter = { studentId: Student['id'] } & Omit<
  StudentWorkbookListParams,
  'page' | 'size' | 'sort'
>
type GroupWorkbookFilter = Omit<GroupStudentWorkbookListParams, 'page' | 'size' | 'sort'>
type WorkbookPageParams = GroupWorkbookPageParams

class LessonWorkbookController implements Controller {
  adapter = {
    studentWorkbook: makeAdapter({
      from: (data: StudentWorkbookListItemDetail) => {
        return new StudentWorkbook(data)
      },
    }),
    studentWorkbookProgress: makeAdapter({
      from: (data: StudentWorkbookPageProgress) => {
        return new StudentWorkbookPage(data)
      },
    }),
    groupWorkbook: makeAdapter({
      from: (data: GroupStudentWorkbook) => {
        return new GroupWorkbook(data)
      },
    }),
    groupWorkbookProgress: makeAdapter({
      from: (data: GroupWorkbookPageProgress) => {
        return new GroupWorkbookPage(data)
      },
    }),
  }

  /*** 학생별 교재 ***/
  // 학생 교재 리스트
  fetchStudentWorkbookColl = async (
    { studentId, ...filter }: StudentWorkbookFilter,
    pageParams: WorkbookPageParams,
  ) => {
    const { content, last } = await studentWorkbookApi.getStudentWorkbookDetails(
      studentId,
      { ...pageParams, ...filter },
      { mp_event_name: mpEventInfo.특정학생출제된교재조회 },
    )

    const workbookColl = ExtendedMap.fromArr(content.map(this.adapter.studentWorkbook.from), 'id')

    return {
      workbookColl,
      last,
    }
  }

  // 학생 진도 리스트
  fetchStudentWorkbookPageColl = async (
    target: StudentWorkbookProgressListTarget,
    pageParams: WorkbookPageParams,
  ) => {
    const { page } = await studentWorkbookApi.getStudentWorkbookProgressList(target, pageParams, {
      mp_event_name: mpEventInfo.특정학생출제된교재진도조회,
    })

    const pageColl = ExtendedMap.fromArr(
      page.content.map(this.adapter.studentWorkbookProgress.from),
      'pageNum',
    )

    return {
      pageColl,
    }
  }

  // 학생 진도별 문제 리스트
  fetchStudentWorkbookPageDetailColl = async (
    target: StudentWorkbookScoredProblemsTarget,
    pageParams: WorkbookPageParams,
  ) => {
    const { content } = await studentWorkbookApi.getStudentWorkbookScoredProblemDetails(
      target,
      pageParams,
      {
        mp_event_name: mpEventInfo.특정학생출제된교재채점조회,
      },
    )

    const detailColl = new StudentWorkbookPageDetailColl(content)

    return {
      detailColl,
    }
  }

  /*** 학년별 교재 ***/
  // 학년 교재 리스트
  fetchGradeWorkbookColl = async (
    { schoolType, grade, ...filter }: GradeStudentWorkbookTarget & GroupWorkbookFilter,
    pageParams: WorkbookPageParams,
  ) => {
    const { content, last } = await studentWorkbookApi.getGradeStudentWorkbooks(
      { schoolType, grade },
      { ...pageParams, ...filter },
      { mp_event_name: mpEventInfo.학년별출제된교재조회 },
    )
    const workbookColl = ExtendedMap.fromArr(content.map(this.adapter.groupWorkbook.from), 'id')

    return {
      workbookColl,
      last,
    }
  }

  // 학년 진도 리스트
  fetchGradeWorkbookPageColl = async (
    target: GradeWorkbookProgressTarget,
    pageParams: WorkbookPageParams,
  ) => {
    const { content, last } = await studentWorkbookApi.getGradeWorkbookPageProgresses(
      target,
      pageParams,
      {
        mp_event_name: mpEventInfo.학년별출제된교재진도조회,
      },
    )

    const pageColl = ExtendedMap.fromArr(
      content.map(this.adapter.groupWorkbookProgress.from),
      'pageNum',
    )

    return { pageColl, last }
  }

  // 학년 페이지별 문제 리스트
  fetchGradeWorkbookPageDetailColl = async (
    target: GradeWorkbookScoringTarget,
    pageParams: WorkbookPageParams,
  ) => {
    const { content } = await studentWorkbookApi.getGradeWorkbookScoredProblems(
      target,
      pageParams,
      {
        mp_event_name: mpEventInfo.학년별출제된교재채점조회,
      },
    )
    return new GroupWorkbookPageDetailColl(content)
  }

  /*** 반별 교재 ***/

  // 반 교재 리스트
  fetchClassWorkbookColl = async (
    { lessonClassId, ...filter }: LessonClassStudentWorkbookTarget & GroupWorkbookFilter,
    pageParams: WorkbookPageParams,
  ) => {
    const { content, last } = await studentWorkbookApi.getLessonClassStudentWorkbooks(
      { lessonClassId },
      { ...pageParams, ...filter },
      { mp_event_name: mpEventInfo.반별출제된교재조회 },
    )

    const workbookColl = ExtendedMap.fromArr(content.map(this.adapter.groupWorkbook.from), 'id')

    return {
      workbookColl,
      last,
    }
  }

  // 반 진도 리스트
  fetchClassWorkbookPageColl = async (
    target: LessonClassWorkbookProgressTarget,
    pageParams: WorkbookPageParams,
  ) => {
    const { content, last } = await studentWorkbookApi.getLessonClassWorkbookPageProgresses(
      target,
      pageParams,
      {
        mp_event_name: mpEventInfo.반별출제된교재진도조회,
      },
    )

    const pageColl = ExtendedMap.fromArr(
      content.map(this.adapter.groupWorkbookProgress.from),
      'pageNum',
    )

    return { pageColl, last }
  }

  // 반 페이지별 문제 리스트
  fetchClassWorkbookPageDetailColl = async (
    target: LessonClassWorkbookScoringTarget,
    pageParams: WorkbookPageParams,
  ) => {
    const { content } = await studentWorkbookApi.getLessonClassWorkbookScoredProblems(
      target,
      pageParams,
      {
        mp_event_name: mpEventInfo.반별출제된교재채점조회,
      },
    )
    return new GroupWorkbookPageDetailColl(content)
  }

  unAssignStudentWorkbook = async (workbookIds: StudentWorkbook['id'][]) =>
    await studentWorkbookApi.deleteStudentWorkbooks(workbookIds, {
      mp_event_name: mpEventInfo.교재출제취소,
    })

  deleteStudentWorkbookRevision = async (revisionIds: StudentWorkbookRevision['id'][]) =>
    await studentWorkbookApi.deleteStudentWorkbookRevisions(revisionIds, {
      mp_event_name: mpEventInfo.특정교재_회차출제취소,
    })

  assignWorkbookStudent = async (data: StudentWorkbookAssignBody) =>
    await studentWorkbookApi.assignStudentWorkbooks(data, {
      mp_event_name: mpEventInfo.교재출제재출제,
    })

  postStudentWorkbookProgressStatus = async (params: StudentWorkbookProgressStatusBody) =>
    studentWorkbookApi.updateStudentWorkbookProgressStatus(params, {
      mp_event_name: mpEventInfo.특정교재_진도상태수정,
    })

  patchStudentWorkbookProblemScoring = async (data: StudentWorkbookScoringPayload) =>
    await studentWorkbookApi.scoreStudentWorkbook(data, { mp_event_name: mpEventInfo.교재문제채점 })
}

export const lessonWorkbookController = new LessonWorkbookController()
